import { Head, router } from "@inertiajs/react"
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  ClipboardList,
  FileText,
  Image,
} from "lucide-react"

import { Breadcrumbs } from "@/components/breadcrumbs"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
  status: number
  case_total: number | null
  to_be_billed: number | null
}

interface ChecklistItem {
  ok: boolean
  // Billing
  amount?: number
  case_total?: number
  // Reports
  unapproved?: number
  // Expenses
  unsubmitted?: number
  // GPS
  installed?: number
  // Schedule
  upcoming?: number
  // Surveillance
  unfinalized?: number
  pending_member_approval?: number
  // Tasks
  pending?: number
  // Time
  unclosed?: number
  unbilled?: number
}

interface Checklist {
  billing: ChecklistItem
  reports: ChecklistItem
  expenses: ChecklistItem
  gps: ChecklistItem
  schedule: ChecklistItem
  surveillance: ChecklistItem
  tasks: ChecklistItem
  time: ChecklistItem
}

interface CostRateProblem {
  time_entry_id: number
  date: string
  service_item: string
  employee: string
  problem: string
}

interface Props {
  case_record: CaseRecord
  checklist: Checklist
  ok_close: boolean
  cost_rate_problems: CostRateProblem[]
}

/* ── Helpers ────────────────────────────────────────────── */

function formatCurrency(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return "$0"
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
  }).format(amount)
}

/* ── Sub-tabs (same as show page) ──────────────────────── */

const caseTabs = [
  { label: "Worksheet", icon: ClipboardList, href: "worksheet" },
  { label: "Activity", icon: Activity, href: "activity" },
  { label: "Reports", icon: FileText, href: "reports" },
  { label: "Documents", icon: FileText, href: "documents" },
  { label: "Case Photos", icon: Image, href: "evidence" },
]

/* ── Status Row Component ──────────────────────────────── */

function StatusRow({
  label,
  ok,
  children,
}: {
  label: string
  ok: boolean
  children?: React.ReactNode
}) {
  return (
    <li className="flex items-start gap-3 py-3">
      {ok ? (
        <CheckCircle2 className="text-hpi-dark-green mt-0.5 h-5 w-5 shrink-0" />
      ) : (
        <AlertTriangle className="text-hpi-red mt-0.5 h-5 w-5 shrink-0" />
      )}
      <div>
        {ok ? (
          <span className="text-hpi-dark-gray text-sm">{label}</span>
        ) : (
          <div className="text-sm">
            <span className="font-semibold">{label}</span>
            {children && <span>: {children}</span>}
          </div>
        )}
      </div>
    </li>
  )
}

/* ── Main Component ────────────────────────────────────── */

export default function Close({
  case_record,
  checklist,
  ok_close,
  cost_rate_problems,
}: Props) {
  const c = case_record
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    { title: c.display_name || c.case_number, href: `/cases/${c.id}` },
    { title: "Close Case", href: `/cases/${c.id}/close` },
  ]

  const caseTabsBar = (
    <div className="flex items-center gap-6">
      {caseTabs.map((tab) => {
        const Icon = tab.icon
        return (
          <a
            key={tab.label}
            href={`/cases/${c.id}/${tab.href}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-hpi-med-blue hover:text-hpi-active-blue flex items-center gap-1.5 text-sm transition-colors"
          >
            <Icon className="h-4 w-4" />
            <span>{tab.label}</span>
          </a>
        )
      })}
    </div>
  )

  function handleClose() {
    router.patch(`/cases/${c.id}/close`)
  }

  return (
    <AppLayout topBar={caseTabsBar}>
      <Head title={`Close Case - ${c.display_name}`} />

      <div className="p-6">
        <div className="mb-6">
          <Breadcrumbs breadcrumbs={breadcrumbs} />
        </div>

        <h1 className="text-hpi-dark-gray mb-6 text-xl font-semibold">
          Close Case: {c.display_name}
        </h1>

        {c.status === 2 ? (
          <div className="bg-hpi-light-red text-hpi-red rounded-md p-4 text-sm">
            This case is already closed.
          </div>
        ) : (
          <>
            <h3 className="text-hpi-dark-gray mb-4 text-lg font-medium">
              Checking for problems...
            </h3>

            <ul className="mb-8 max-w-2xl divide-y">
              {/* Billing */}
              <StatusRow label="Billing" ok={checklist.billing.ok}>
                <a
                  href={`/cases/${c.id}/time_entries`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline"
                >
                  <span className="text-hpi-red">
                    {formatCurrency(checklist.billing.amount)}
                  </span>{" "}
                  of {formatCurrency(checklist.billing.case_total)} needs to be
                  billed
                </a>
              </StatusRow>

              {/* Daily Reports */}
              <StatusRow label="Daily Reports" ok={checklist.reports.ok}>
                <a
                  href={`/cases/${c.id}/reports`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline"
                >
                  <span className="text-hpi-red">
                    {checklist.reports.unapproved}
                  </span>{" "}
                  Unapproved
                </a>
              </StatusRow>

              {/* Expenses */}
              <StatusRow label="Expenses" ok={checklist.expenses.ok}>
                <span className="space-x-3">
                  {(checklist.expenses.unsubmitted ?? 0) > 0 && (
                    <a
                      href={`/cases/${c.id}/expenses`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline"
                    >
                      <span className="text-hpi-red">
                        {formatCurrency(checklist.expenses.unsubmitted)}
                      </span>{" "}
                      Unsubmitted
                    </a>
                  )}
                  {(checklist.expenses.unapproved ?? 0) > 0 && (
                    <a
                      href={`/cases/${c.id}/expenses`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline"
                    >
                      <span className="text-hpi-red">
                        {formatCurrency(checklist.expenses.unapproved)}
                      </span>{" "}
                      Unapproved
                    </a>
                  )}
                  {(checklist.expenses.unbilled ?? 0) > 0 && (
                    <a
                      href={`/cases/${c.id}/expenses`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline"
                    >
                      <span className="text-hpi-red">
                        {formatCurrency(checklist.expenses.unbilled)}
                      </span>{" "}
                      Unbilled
                    </a>
                  )}
                </span>
              </StatusRow>

              {/* GPS */}
              <StatusRow label="GPS" ok={checklist.gps.ok}>
                <a
                  href="/gps_devices"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline"
                >
                  <span className="text-hpi-red">
                    {checklist.gps.installed}
                  </span>{" "}
                  Installed
                </a>
              </StatusRow>

              {/* Schedule */}
              <StatusRow label="Schedule" ok={checklist.schedule.ok}>
                <a
                  href={`/cases/${c.id}/schedules`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline"
                >
                  <span className="text-hpi-red">
                    {checklist.schedule.upcoming}
                  </span>{" "}
                  Upcoming Scheduled Work
                </a>
              </StatusRow>

              {/* Surveillance */}
              <StatusRow label="Surveillance" ok={checklist.surveillance.ok}>
                <span className="space-x-3">
                  {(checklist.surveillance.unfinalized ?? 0) > 0 && (
                    <a
                      href={`/cases/${c.id}/reports`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline"
                    >
                      <span className="text-hpi-red">
                        {checklist.surveillance.unfinalized}
                      </span>{" "}
                      Unfinalized
                    </a>
                  )}
                  {(checklist.surveillance.pending_member_approval ?? 0) >
                    0 && (
                    <a
                      href={`/cases/${c.id}/reports`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline"
                    >
                      <span className="text-hpi-red">
                        {checklist.surveillance.pending_member_approval}
                      </span>{" "}
                      Pending Member Approval
                    </a>
                  )}
                  {(checklist.surveillance.unapproved ?? 0) > 0 && (
                    <a
                      href={`/cases/${c.id}/reports`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline"
                    >
                      <span className="text-hpi-red">
                        {checklist.surveillance.unapproved}
                      </span>{" "}
                      Unapproved
                    </a>
                  )}
                </span>
              </StatusRow>

              {/* Tasks */}
              <StatusRow label="Tasks" ok={checklist.tasks.ok}>
                <a
                  href={`/cases/${c.id}/logs`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline"
                >
                  <span className="text-hpi-red">
                    {checklist.tasks.pending}
                  </span>{" "}
                  Pending
                </a>
              </StatusRow>

              {/* Time */}
              <StatusRow label="Time" ok={checklist.time.ok}>
                <span className="space-x-3">
                  {(checklist.time.unclosed ?? 0) > 0 && (
                    <a
                      href={`/cases/${c.id}/time_entries`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline"
                    >
                      <span className="text-hpi-red">
                        {checklist.time.unclosed}
                      </span>{" "}
                      Unclosed
                    </a>
                  )}
                  {(checklist.time.unapproved ?? 0) > 0 && (
                    <a
                      href={`/cases/${c.id}/time_entries`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline"
                    >
                      <span className="text-hpi-red">
                        {checklist.time.unapproved}
                      </span>{" "}
                      Unapproved
                    </a>
                  )}
                  {(checklist.time.unbilled ?? 0) > 0 && (
                    <a
                      href={`/cases/${c.id}/time_entries`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline"
                    >
                      <span className="text-hpi-red">
                        {checklist.time.unbilled}
                      </span>{" "}
                      Unbilled
                    </a>
                  )}
                </span>
              </StatusRow>
            </ul>

            {/* Cost/Rate Problems */}
            {cost_rate_problems.length > 0 && (
              <div className="mb-8 max-w-3xl">
                <h3 className="text-hpi-dark-gray mb-3 text-lg font-medium">
                  Cost/Rate Problems
                </h3>
                <div className="overflow-hidden rounded-md border">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 font-medium">Date</th>
                        <th className="px-4 py-2 font-medium">Type</th>
                        <th className="px-4 py-2 font-medium">Employee</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {cost_rate_problems.map((p, idx) => (
                        <tr key={idx}>
                          <td className="px-4 py-2">
                            <a
                              href={`/cases/${c.id}/time_entries`}
                              className="text-hpi-med-blue hover:underline"
                            >
                              {p.date}
                            </a>
                          </td>
                          <td className="px-4 py-2">
                            <a
                              href={`/cases/${c.id}/time_entries`}
                              className="text-hpi-med-blue hover:underline"
                            >
                              {p.service_item}
                            </a>
                          </td>
                          <td className="px-4 py-2">
                            <a
                              href={`/cases/${c.id}/time_entries`}
                              className="text-hpi-med-blue hover:underline"
                            >
                              {p.employee}
                            </a>
                          </td>
                          <td className="px-4 py-1 text-xs">
                            <span className="text-hpi-red">{p.problem}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Close button or error */}
            {ok_close ? (
              <div className="text-center">
                <button
                  onClick={handleClose}
                  className="bg-hpi-dark-blue hover:bg-hpi-active-blue rounded-md px-6 py-2 text-sm font-medium text-white transition-colors"
                >
                  Close this Case
                </button>
              </div>
            ) : (
              <div className="bg-hpi-light-red text-hpi-red rounded-md p-4 text-sm">
                This case can&apos;t be closed until the items above have been
                completed.
              </div>
            )}
          </>
        )}
      </div>
    </AppLayout>
  )
}
